/**
 * AnalysisProgressOverlay - Fortschrittsanzeige waehrend Batch-Analyse
 *
 * Pollt den Status alle 2s, zeigt animierten Fortschritt, Zwischenergebnisse
 * und nach Abschluss eine Erfolg/Misserfolg-Aufschlüsselung.
 */

import { Loader2, CheckCircle2, XCircle, AlertCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button, Progress, Badge } from '@/components/v2/ui'
import { useAnalysisStatus, useCancelAnalysis } from '@/hooks/useImageIntelligence'

export interface AnalysisProgressOverlayProps {
  runId: string
  onComplete?: () => void
  onCancel?: () => void
}

const CATEGORY_VARIANTS: Record<string, 'success' | 'warning' | 'error' | 'default' | 'info'> = {
  packshot: 'success',
  lifestyle: 'warning',
  detail: 'info',
  mood: 'default',
  brand: 'default',
  other: 'default',
  failed: 'error',
  pending: 'default',
}

export function AnalysisProgressOverlay({ runId, onComplete, onCancel }: AnalysisProgressOverlayProps) {
  const { t } = useTranslation()
  const { data: run } = useAnalysisStatus(runId)
  const cancelMutation = useCancelAnalysis()

  const isRunning = run?.status === 'pending' || run?.status === 'running'
  const isCompleted = run?.status === 'completed'
  const isFailed = run?.status === 'failed'
  const isDone = isCompleted || isFailed || run?.status === 'cancelled'
  // Fix: Backend liefert 0% wenn done → bei completed immer 100%
  const percent = isCompleted ? 100 : (run?.progress_percent ?? 0)
  const current = run?.progress_current ?? 0
  const total = run?.progress_total ?? 0
  const remaining = run?.estimated_remaining_seconds
  const summary = run?.results_summary

  const handleCancel = (): void => {
    cancelMutation.mutate(runId, { onSuccess: () => onCancel?.() })
  }

  return (
    <div className="analysis-progress space-y-5">
      <ProgressHeader isRunning={isRunning} isCompleted={isCompleted} isFailed={isFailed} />

      <div className="space-y-2">
        <Progress
          value={percent}
          variant={isFailed ? 'error' : isCompleted ? 'success' : 'default'}
          size="md"
          showLabel
          label={`${current} / ${total}`}
        />
        {isRunning && remaining !== undefined && remaining > 0 && (
          <p className="text-xs text-neutral-500">
            {t('readiness.estimatedRemaining', { minutes: Math.ceil(remaining / 60) })}
          </p>
        )}
      </div>

      {isRunning && run?.last_analyzed && (
        <div className="analysis-progress-last text-sm text-neutral-600 dark:text-neutral-400">
          {t('readiness.lastAnalyzedLabel')}: &quot;{run.last_analyzed.product_name}&quot; →{' '}
          <span className="font-medium">{run.last_analyzed.score}</span>{' '}
          <span className="uppercase text-xs">{run.last_analyzed.category}</span>
        </div>
      )}

      {summary && Object.keys(summary).length > 0 && (
        isDone
          ? <AnalysisResultBreakdown summary={summary} />
          : <InterimResultsBadges summary={summary} />
      )}

      <div className="analysis-progress-actions flex justify-end gap-2 pt-2 border-t border-neutral-200 dark:border-neutral-700">
        {isRunning && (
          <Button variant="outline" size="sm" onClick={handleCancel} disabled={cancelMutation.isPending}>
            {cancelMutation.isPending ? t('readiness.cancellingAnalysis') : t('readiness.cancelAnalysis')}
          </Button>
        )}
        {isDone && (
          <Button variant="primary" size="sm" onClick={onComplete}>
            {t('common.close')}
          </Button>
        )}
      </div>
    </div>
  )
}

function ProgressHeader({ isRunning, isCompleted, isFailed }: {
  isRunning: boolean
  isCompleted: boolean
  isFailed: boolean
}) {
  const { t } = useTranslation()
  const isDone = isCompleted || isFailed
  return (
    <div className={`analysis-progress-header flex items-center gap-3 p-3 rounded-lg transition-all ${
      isRunning
        ? 'bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800'
        : isCompleted
          ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
          : isFailed
            ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
            : 'bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700'
    }`}>
      {isRunning && <Loader2 className="h-5 w-5 text-primary-500 animate-spin flex-shrink-0" aria-hidden="true" />}
      {isCompleted && <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" aria-hidden="true" />}
      {isFailed && <XCircle className="h-5 w-5 text-red-500 flex-shrink-0" aria-hidden="true" />}
      {!isRunning && !isDone && <AlertCircle className="h-5 w-5 text-neutral-400 flex-shrink-0" aria-hidden="true" />}
      <div>
        <h3 className="text-base font-semibold text-neutral-900 dark:text-neutral-100">
          {isDone ? t('readiness.analysisDone') : t('readiness.analysisRunning')}
        </h3>
        {isRunning && (
          <p className="text-xs text-primary-600 dark:text-primary-400">KI klassifiziert Ihre Bilder…</p>
        )}
      </div>
    </div>
  )
}

function AnalysisResultBreakdown({ summary }: { summary: Record<string, number> }) {
  const { t } = useTranslation()
  const SUCCESS_KEYS = ['packshot', 'lifestyle', 'detail', 'mood', 'brand', 'other']
  const failed = summary.failed ?? 0
  const successCount = SUCCESS_KEYS.reduce((acc, k) => acc + (summary[k] ?? 0), 0)
  const total = successCount + failed
  const successRate = total > 0 ? Math.round((successCount / total) * 100) : 0

  return (
    <div className="analysis-progress-result rounded-lg border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800/50 p-3 space-y-3">
      <h4 className="text-xs font-semibold uppercase tracking-wider text-neutral-500">
        {t('readiness.analysisBreakdown')}
      </h4>
      <div className="grid grid-cols-3 gap-2 text-center">
        <ResultStat
          label={t('readiness.analysisSuccess')}
          value={successCount}
          color="text-green-600 dark:text-green-400"
        />
        <ResultStat
          label={t('readiness.analysisFailed')}
          value={failed}
          color={failed > 0 ? 'text-red-600 dark:text-red-400' : 'text-neutral-500'}
        />
        <ResultStat
          label={t('readiness.successRate')}
          value={`${successRate}%`}
          color={successRate >= 80 ? 'text-green-600 dark:text-green-400' : successRate >= 50 ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400'}
        />
      </div>
      <CategoryBadges summary={summary} />
    </div>
  )
}

function ResultStat({ label, value, color }: { label: string; value: string | number; color: string }) {
  return (
    <div className="analysis-result-stat p-2 rounded bg-white dark:bg-neutral-700/50 border border-neutral-100 dark:border-neutral-600">
      <div className={`text-lg font-bold tabular-nums ${color}`}>{value}</div>
      <div className="text-xs text-neutral-500 mt-0.5">{label}</div>
    </div>
  )
}

function InterimResultsBadges({ summary }: { summary: Record<string, number> }) {
  const { t } = useTranslation()
  const CATEGORY_LABELS: Record<string, string> = {
    packshot: t('readiness.categoryPackshot'),
    lifestyle: t('readiness.categoryLifestyle'),
    detail: t('readiness.categoryDetail'),
    mood: t('readiness.categoryMood'),
    brand: t('readiness.categoryBrand'),
    other: t('readiness.categoryOther'),
    failed: t('readiness.categoryFailed'),
    pending: t('readiness.categoryPending'),
  }
  const entries = Object.entries(summary).filter(([, count]) => count > 0).sort(([, a], [, b]) => b - a)
  return (
    <div className="analysis-progress-interim rounded-lg border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800/50 p-3">
      <h4 className="text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-2">
        {t('readiness.interimResults')}
      </h4>
      <div className="flex flex-wrap gap-2">
        {entries.map(([key, count]) => (
          <Badge key={key} variant={CATEGORY_VARIANTS[key] || 'default'} className="gap-1">
            {CATEGORY_LABELS[key] || key}
            <span className="font-bold tabular-nums">{count}</span>
          </Badge>
        ))}
      </div>
    </div>
  )
}

function CategoryBadges({ summary }: { summary: Record<string, number> }) {
  const { t } = useTranslation()
  const CATEGORY_LABELS: Record<string, string> = {
    packshot: t('readiness.categoryPackshot'),
    lifestyle: t('readiness.categoryLifestyle'),
    detail: t('readiness.categoryDetail'),
    mood: t('readiness.categoryMood'),
    brand: t('readiness.categoryBrand'),
    other: t('readiness.categoryOther'),
    failed: t('readiness.categoryFailed'),
  }
  const entries = Object.entries(summary).filter(([key, count]) => count > 0 && key !== 'pending').sort(([, a], [, b]) => b - a)
  if (!entries.length) return null
  return (
    <div className="flex flex-wrap gap-1.5">
      {entries.map(([key, count]) => (
        <Badge key={key} variant={CATEGORY_VARIANTS[key] || 'default'} className="gap-1 text-xs">
          {CATEGORY_LABELS[key] || key}
          <span className="font-bold tabular-nums">{count}</span>
        </Badge>
      ))}
    </div>
  )
}
